import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';

const AuthContext = createContext();

const ACCOUNTS_BASE = process.env.REACT_APP_LAZPLAY_ACCOUNTS_URL || 'https://dev-accounts.lazplay.in';
const CLIENT_ID = process.env.REACT_APP_LAZPLAY_CLIENT_ID || 'lz_client_forma';
const API_BASE =
  process.env.REACT_APP_API_URL ||
  (window.location.hostname === 'localhost' ? 'http://localhost:3000' : 'https://api.forma.lazplay.in');

// Helper: Base64URL encoding for ArrayBuffer / Uint8Array
function bufferToBase64Url(buffer) {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary)
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
}

// Helper: Generate PKCE code verifier and code challenge (S256)
async function generatePkcePair() {
  const randomBytes = new Uint8Array(32);
  window.crypto.getRandomValues(randomBytes);
  const codeVerifier = bufferToBase64Url(randomBytes);

  const encoder = new TextEncoder();
  const data = encoder.encode(codeVerifier);
  const digest = await window.crypto.subtle.digest('SHA-256', data);
  const codeChallenge = bufferToBase64Url(digest);

  const stateBytes = new Uint8Array(16);
  window.crypto.getRandomValues(stateBytes);
  const state = bufferToBase64Url(stateBytes);

  return { codeVerifier, codeChallenge, state };
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(() => {
    const saved = localStorage.getItem('forma_user');
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (e) {
        return null;
      }
    }
    return null;
  });

  const [token, setToken] = useState(() => localStorage.getItem('forma_token') || '');
  const [refreshToken, setRefreshToken] = useState(() => localStorage.getItem('forma_refresh_token') || '');
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const [authError, setAuthError] = useState(null);

  const hasHandledCallbackRef = useRef(false);
  const isAuthenticated = !!user;

  // Persist user & tokens
  useEffect(() => {
    if (user) {
      localStorage.setItem('forma_user', JSON.stringify(user));
    } else {
      localStorage.removeItem('forma_user');
    }
  }, [user]);

  useEffect(() => {
    if (token) {
      localStorage.setItem('forma_token', token);
    } else {
      localStorage.removeItem('forma_token');
    }
  }, [token]);

  useEffect(() => {
    if (refreshToken) {
      localStorage.setItem('forma_refresh_token', refreshToken);
    } else {
      localStorage.removeItem('forma_refresh_token');
    }
  }, [refreshToken]);

  // 1. Initiate Lazplay Accounts OAuth 2.0 PKCE Flow
  const loginWithLazplay = async () => {
    setAuthError(null);
    try {
      const { codeVerifier, codeChallenge, state } = await generatePkcePair();

      // Store PKCE verifier & state in session storage AND local storage for cross-redirect resilience
      sessionStorage.setItem('lz_pkce_verifier', codeVerifier);
      sessionStorage.setItem('lz_pkce_state', state);
      localStorage.setItem('lz_pkce_verifier', codeVerifier);
      localStorage.setItem('lz_pkce_state', state);

      const redirectUri = window.location.origin + window.location.pathname;
      sessionStorage.setItem('lz_pkce_redirect_uri', redirectUri);
      localStorage.setItem('lz_pkce_redirect_uri', redirectUri);

      const params = new URLSearchParams({
        client_id: CLIENT_ID,
        redirect_uri: redirectUri,
        response_type: 'code',
        scope: 'openid profile email',
        code_challenge: codeChallenge,
        code_challenge_method: 'S256',
        state: state
      });

      const authorizeUrl = `${ACCOUNTS_BASE}/api/v1/oauth/authorize?${params.toString()}`;
      window.location.href = authorizeUrl;
    } catch (err) {
      console.error('[Lazplay Auth Init Error]', err);
      setAuthError('Failed to initiate Lazplay sign in.');
    }
  };

  // 2. Handle OAuth Callback via Backend API Route (Unified, Server-Side, CORS-Free)
  const handleOAuthCallback = useCallback(async (code, returnedState) => {
    if (hasHandledCallbackRef.current) return;
    hasHandledCallbackRef.current = true;

    setIsAuthenticating(true);
    setAuthError(null);

    try {
      const storedVerifier =
        sessionStorage.getItem('lz_pkce_verifier') || localStorage.getItem('lz_pkce_verifier');
      const redirectUri =
        sessionStorage.getItem('lz_pkce_redirect_uri') ||
        localStorage.getItem('lz_pkce_redirect_uri') ||
        window.location.origin + window.location.pathname;

      // Call Unified Backend OAuth Callback Route
      const response = await fetch(`${API_BASE}/api/v1/auth/oauth/callback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          code: code,
          code_verifier: storedVerifier || undefined,
          redirect_uri: redirectUri,
          client_id: CLIENT_ID
        })
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.error || 'Failed to authenticate with Lazplay Accounts.');
      }

      const activeUser = data.user;
      const sessionToken = data.token;
      const newRefreshToken = data.refreshToken || '';

      // Clean up session & local storage PKCE keys
      sessionStorage.removeItem('lz_pkce_verifier');
      sessionStorage.removeItem('lz_pkce_state');
      sessionStorage.removeItem('lz_pkce_redirect_uri');
      localStorage.removeItem('lz_pkce_verifier');
      localStorage.removeItem('lz_pkce_state');
      localStorage.removeItem('lz_pkce_redirect_uri');

      // Update active state
      setToken(sessionToken);
      if (newRefreshToken) setRefreshToken(newRefreshToken);
      setUser(activeUser);

      // Clean query parameters from URL without reloading page
      const cleanUrl = window.location.origin + window.location.pathname;
      window.history.replaceState({}, document.title, cleanUrl);

      return { success: true, user: activeUser };
    } catch (err) {
      console.error('[OAuth Callback Error]', err);
      setAuthError(err.message || 'Authentication with Lazplay Accounts failed.');
      // Clean query params so user can retry
      const cleanUrl = window.location.origin + window.location.pathname;
      window.history.replaceState({}, document.title, cleanUrl);
      throw err;
    } finally {
      setIsAuthenticating(false);
    }
  }, []);

  // Auto-detect OAuth redirect parameters on load
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');
    const state = urlParams.get('state');
    const error = urlParams.get('error');
    const errorDesc = urlParams.get('error_description');

    if (error) {
      setAuthError(errorDesc || error);
      const cleanUrl = window.location.origin + window.location.pathname;
      window.history.replaceState({}, document.title, cleanUrl);
      return;
    }

    if (code && !hasHandledCallbackRef.current) {
      handleOAuthCallback(code, state).catch(() => {});
    }
  }, [handleOAuthCallback]);

  // 3. Sign out & Token Revocation
  const logout = async () => {
    if (token) {
      try {
        fetch(`${API_BASE}/api/v1/auth/oauth/revoke`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token, client_id: CLIENT_ID })
        }).catch(() => {});
      } catch (e) {}
    }

    setUser(null);
    setToken('');
    setRefreshToken('');
    hasHandledCallbackRef.current = false;
    localStorage.removeItem('forma_user');
    localStorage.removeItem('forma_token');
    localStorage.removeItem('forma_refresh_token');
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        isAuthenticated,
        isAuthenticating,
        authError,
        loginWithLazplay,
        handleOAuthCallback,
        logout
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
